import { AttributesList, createAttributesItem as createItem } from 'src/utils/createAttributesItem.ts';
import { ActuatorData, ValveData } from 'src/modules/checkout/CheckoutPageMapping.ts';
import { DashValue } from 'src/modules/valves/ValveCard/DashValue.ts';
import { filterEmpty } from 'src/utils/filterEmpty.ts';
import { AttributesAliases } from 'src/generated/dict.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';

export function prepareComboAttributes(
    valve: ValveData,
    actuator: ActuatorData,
    convertVnom: (vnom: string) => ({ raw: string[]; localized: string[]} | null),
): [AttributesList, AttributesList] {
    let attrs: [AttributesList, AttributesList] = [[], []];
    const product: ActuatorData = actuator || valve;
    const isEffectivePV2Meaningful = valve.calcEffectivePV2 && valve.calcEffectivePV2 !== valve.calcEffectivePV;
    const is6way = valve.pattern?.raw.join('').includes('6');
    if ('kvs' in valve) {
        const clipPosKvs = valve.getClipPositionKvs
            ? { raw: [valve.getClipPositionKvs], localized: [valve.getClipPositionKvs] } : null;
        attrs = [
            [
                [AttributesAliases.dn, valve.dn],
                ...(valve.getClipPosition ? createItem(
                    MESSAGES.clipPosition,
                    { raw: [valve.getClipPosition], localized: [valve.getClipPosition] },
                ) : []),
                ...(valve.getClipPositionPI ? createItem(
                    MESSAGES.clipPosition,
                    { raw: [valve.getClipPositionPI], localized: [valve.getClipPositionPI] },
                ) : []),
                ...createItem(
                    is6way ? AttributesAliases.kvssequence1 : AttributesAliases.kvs,
                    clipPosKvs || valve.kvs || valve.kvssequence1,
                ),
                ...createItem(AttributesAliases.kvmax, valve.kvmax),
                ...createItem(
                    AttributesAliases.vnomls,
                    convertVnom(valve.vnomm3h?.raw.join('') ?? ''),
                ),
                ...createItem(AttributesAliases.kvssequence2, valve.kvssequence2),
                ...(valve.calcEffectiveDpv ? createItem(
                    MESSAGES.EffectiveDpv,
                    { raw: [valve.calcEffectiveDpv], localized: [valve.calcEffectiveDpv] },
                ) : []),
                ...(is6way && valve.calcEffectiveDpv6way1 ? createItem(
                    MESSAGES.EffectiveDpv1,
                    { raw: [valve.calcEffectiveDpv6way1], localized: [valve.calcEffectiveDpv6way1] },
                ) : []),
                ...(is6way && valve.calcEffectiveDpv6way2 ? createItem(
                    MESSAGES.EffectiveDpv2,
                    { raw: [valve.calcEffectiveDpv6way2], localized: [valve.calcEffectiveDpv6way2] },
                ) : []),
                ...(valve.calcMinReqDpv ? createItem(
                    is6way ? MESSAGES.MinReqDpv1 : MESSAGES.MinReqDpv,
                    { raw: [valve.calcMinReqDpv], localized: [valve.calcMinReqDpv] },
                ) : []),
                ...(is6way && valve.calcMinReqDpv2 ? createItem(
                    MESSAGES.MinReqDpv2,
                    { raw: [valve.calcMinReqDpv2], localized: [valve.calcMinReqDpv2] },
                ) : []),
                ...(valve.calcEffectivePV ? createItem(
                    MESSAGES.EffectivePV,
                    { raw: [valve.calcEffectivePV], localized: [valve.calcEffectivePV] },
                ) : []),
                ...(is6way && valve.calcEffectivePV1 && isEffectivePV2Meaningful ? createItem(
                    MESSAGES.EffectivePV1,
                    { raw: [valve.calcEffectivePV1], localized: [valve.calcEffectivePV1] },
                ) : []),
                ...(is6way && valve.calcEffectivePV2 && isEffectivePV2Meaningful ? createItem(
                    MESSAGES.EffectivePV2,
                    { raw: [valve.calcEffectivePV2], localized: [valve.calcEffectivePV2] },
                ) : []),
                ...(valve.calcFlowVelocity ? createItem(
                    is6way ? MESSAGES.FlowVelocity1 : MESSAGES.FlowVelocity,
                    { raw: [valve.calcFlowVelocity], localized: [valve.calcFlowVelocity] },
                ) : []),
                ...(is6way && valve.calcFlowVelocity2 ? createItem(
                    MESSAGES.FlowVelocity2,
                    { raw: [valve.calcFlowVelocity2], localized: [valve.calcFlowVelocity2] },
                ) : []),
                [AttributesAliases.pipeConnection, valve.pipeConnection],
            ], [
                [AttributesAliases.nominalVoltage, product.nominalVoltage || DashValue],
                [AttributesAliases.controlType, product.controlType || DashValue],
                [AttributesAliases.runningTime, product.runningTime || DashValue],
                [AttributesAliases.failSafeFunction, product.failSafeFunction || DashValue],
            ]];
    }
    if ('cv' in valve) {
        const clipPosKvs = valve.getClipPositionKvs
            ? { raw: [valve.getClipPositionKvs], localized: [valve.getClipPositionKvs] } : null;
        attrs = [
            [
                [AttributesAliases.bodySize, valve.bodySize],
                ...(valve.getClipPosition ? createItem(
                    MESSAGES.clipPosition,
                    { raw: [valve.getClipPosition], localized: [valve.getClipPosition] },
                ) : []),
                ...(valve.getClipPositionPI ? createItem(
                    MESSAGES.clipPosition,
                    { raw: [valve.getClipPositionPI], localized: [valve.getClipPositionPI] },
                ) : []),
                ...createItem(
                    AttributesAliases.cv,
                    clipPosKvs || valve.cv,
                ),
                ...createItem(
                    AttributesAliases.vnomgpm,
                    valve.vnomgpm,
                ),
                ...createItem(AttributesAliases.cvsequence1, valve.cvsequence1),
                ...createItem(AttributesAliases.cvsequence2, valve.cvsequence2),
                ...(valve.calcEffectiveDpv ? createItem(
                    MESSAGES.EffectiveDpv,
                    { raw: [valve.calcEffectiveDpv], localized: [valve.calcEffectiveDpv] },
                ) : []),
                ...(is6way && valve.calcEffectiveDpv6way1 ? createItem(
                    MESSAGES.EffectiveDpv1,
                    { raw: [valve.calcEffectiveDpv6way1], localized: [valve.calcEffectiveDpv6way1] },
                ) : []),
                ...(is6way && valve.calcEffectiveDpv6way2 ? createItem(
                    MESSAGES.EffectiveDpv2,
                    { raw: [valve.calcEffectiveDpv6way2], localized: [valve.calcEffectiveDpv6way2] },
                ) : []),
                ...(valve.calcMinReqDpv ? createItem(
                    is6way ? MESSAGES.MinReqDpv1 : MESSAGES.MinReqDpv,
                    { raw: [valve.calcMinReqDpv], localized: [valve.calcMinReqDpv] },
                ) : []),
                ...(is6way && valve.calcMinReqDpv2 ? createItem(
                    MESSAGES.MinReqDpv2,
                    { raw: [valve.calcMinReqDpv2], localized: [valve.calcMinReqDpv2] },
                ) : []),
                ...(valve.calcEffectivePV ? createItem(
                    MESSAGES.EffectivePV,
                    { raw: [valve.calcEffectivePV], localized: [valve.calcEffectivePV] },
                ) : []),
                ...(is6way && valve.calcEffectivePV1 && isEffectivePV2Meaningful ? createItem(
                    MESSAGES.EffectivePV2,
                    { raw: [valve.calcEffectivePV1], localized: [valve.calcEffectivePV1] },
                ) : []),
                ...(is6way && valve.calcEffectivePV2 && isEffectivePV2Meaningful ? createItem(
                    MESSAGES.EffectivePV2,
                    { raw: [valve.calcEffectivePV2], localized: [valve.calcEffectivePV2] },
                ) : []),
                ...(valve.calcFlowVelocity ? createItem(
                    is6way ? MESSAGES.FlowVelocity1 : MESSAGES.FlowVelocity,
                    { raw: [valve.calcFlowVelocity], localized: [valve.calcFlowVelocity] },
                ) : []),
                ...(is6way && valve.calcFlowVelocity2 ? createItem(
                    MESSAGES.FlowVelocity2,
                    { raw: [valve.calcFlowVelocity2], localized: [valve.calcFlowVelocity2] },
                ) : []),
            ], [
                [AttributesAliases.nominalVoltage, product.nominalVoltage || DashValue],
                [AttributesAliases.controlType, product.controlType || DashValue],
                [AttributesAliases.runningTime, product.runningTime || DashValue],
                [AttributesAliases.failSafeFunction, product.failSafeFunction || DashValue],
            ],
        ];
    }
    return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
}
